import React, { useMemo } from 'react'
import { useOutletContext } from 'react-router-dom'
import { Card, Typography, Flex, Form, Input, Select, message } from 'antd'
import { CopyOutlined } from '@ant-design/icons'
import { getFunnelsURL } from '@/api/client'
import type { LeadFlow } from '@/data'
import type { TestEditorContext } from '../../index'

const { Title, Text } = Typography

// Builds dropdown options from flows, prepending `selected` so labels render
const buildFlowOptions = (
  selected: Array<{ id: string; name: string }>,
  flows: LeadFlow[],
) => {
  const map = new Map<string, string>()
  for (const s of selected) map.set(s.id, s.name)
  for (const f of flows) map.set(String(f.id), f.name)
  return Array.from(map, ([value, label]) => ({ value, label }))
}

export const TestInfoPage: React.FC = () => {
  const {
    testName,
    setTestName,
    baseFlow,
    setBaseFlow,
    leadFlows,
    setLeadFlows,
    availableFlows,
    searchFlows,
  } = useOutletContext<TestEditorContext>()

  const testFlowIds = leadFlows.map((f) => f.flow_id)

  const handleFlowChange = (flowIds: string[]) => {
    if (flowIds.length === 0) {
      setLeadFlows([])
      return
    }

    const equalShare = Math.floor(100 / flowIds.length)
    const remainder = 100 - equalShare * flowIds.length

    setLeadFlows(
      flowIds.map((id, index) => {
        const existing = leadFlows.find((f) => f.flow_id === id)
        const flow = availableFlows.find((f) => String(f.id) === id)
        return {
          flow_id: id,
          flow_name: existing?.flow_name || flow?.name || id,
          traffic_percentage:
            existing?.traffic_percentage ??
            equalShare + (index === flowIds.length - 1 ? remainder : 0),
        }
      }),
    )
  }

  const handleBaseFlowChange = (value: string | undefined) => {
    if (!value) {
      setBaseFlow(null)
      return
    }
    const found = availableFlows.find((f) => String(f.id) === value)
    if (found) {
      setBaseFlow({ id: value, name: found.name, slug: found.slug })
    }

    if (testFlowIds.includes(value)) {
      handleFlowChange(testFlowIds.filter((id) => id !== value))
    }
  }

  const baseFlowOptions = useMemo(
    () => buildFlowOptions(baseFlow ? [baseFlow] : [], availableFlows),
    [availableFlows, baseFlow],
  )

  const testFlowOptions = useMemo(
    () =>
      buildFlowOptions(
        leadFlows.map((lf) => ({ id: lf.flow_id, name: lf.flow_name })),
        availableFlows.filter((f) => String(f.id) !== baseFlow?.id),
      ),
    [availableFlows, leadFlows, baseFlow],
  )

  return (
    <div>
      <Flex vertical gap="large" style={{ width: '100%' }}>
        <div>
          <Title level={4}>Test Information</Title>
          <Text type="secondary">
            Name your test and select the lead flows to compare.
          </Text>
        </div>

        <Card title="Test Details" style={{ maxWidth: 700 }}>
          <Form layout="vertical">
            <Form.Item label="Test Name">
              <Input
                value={testName}
                onChange={(e) => setTestName(e.target.value)}
                placeholder="Enter test name"
                size="large"
              />
            </Form.Item>

            <Form.Item label="Control Flow">
              <Select
                showSearch
                allowClear
                placeholder="Search by name..."
                value={baseFlow?.id}
                onChange={handleBaseFlowChange}
                onSearch={searchFlows}
                filterOption={false}
                style={{ width: '100%' }}
                size="large"
                options={baseFlowOptions}
              />
            </Form.Item>

            {baseFlow?.slug &&
              (() => {
                const testUrl = `${getFunnelsURL()}/_/${baseFlow.slug}`
                return (
                  <Form.Item label="Test URL">
                    <Input
                      readOnly
                      value={testUrl}
                      size="large"
                      addonAfter={
                        <CopyOutlined
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            navigator.clipboard.writeText(testUrl)
                            message.success('URL copied')
                          }}
                        />
                      }
                    />
                  </Form.Item>
                )
              })()}

            <Form.Item label="Test Flows">
              <Select
                mode="multiple"
                placeholder="Search by name..."
                value={testFlowIds}
                onChange={handleFlowChange}
                onSearch={searchFlows}
                filterOption={false}
                style={{ width: '100%' }}
                size="large"
                options={testFlowOptions}
              />
              {testFlowIds.length > 0 && (
                <Text type="secondary" style={{ fontSize: 12, marginTop: 4 }}>
                  {testFlowIds.length} flow
                  {testFlowIds.length !== 1 ? 's' : ''} selected — traffic will
                  be split equally by default
                </Text>
              )}
            </Form.Item>
          </Form>
        </Card>
      </Flex>
    </div>
  )
}
